import moment from 'moment';

const DOB_FIELD_TOKENS = ['dob', 'date_of_birth', 'birth_date', 'birthdate'];

const WORKFLOW_ICON_TYPES = {
  create_chart: 'create',
  send_to_chart: 'send',
  task_creation: 'task',
};

// Coerces an incoming value for a `date` field into the moment the antd
// DatePicker expects, and returns the value untouched when it is not a date the
// caller's formats recognize — an unparseable prefill is surfaced to the user
// rather than silently dropped. Strict parsing throughout: a loose parse turns
// arbitrary text into a plausible-looking wrong date.
//
// `formats` stays a parameter because the call sites genuinely disagree about
// which spellings they accept, and quietly widening one of them would change
// which prefills survive.
const parseDateFieldValue = (value, formats) => {
  if (moment.isMoment(value)) return value;

  if (value instanceof Date) {
    const parsedFromDate = moment(value);
    return parsedFromDate.isValid() ? parsedFromDate : value;
  }

  const parsed = moment(value, formats, true);
  return parsed.isValid() ? parsed : value;
};

const buildFieldMatcher = (tokens) => (field) => {
  const fieldName = (field?.internal_name || '').toLowerCase();
  if (!fieldName) {
    return false;
  }
  return tokens.some((token) => fieldName.includes(token));
};

const coalescePatientValue = (patient, keys = []) => {
  if (!patient) {
    return undefined;
  }

  return [patient.metadata, patient].reduce((matchedValue, source) => {
    if (matchedValue !== undefined) {
      return matchedValue;
    }
    if (!source) {
      return undefined;
    }

    const matchedKey = keys.find((key) => {
      const value = source?.[key];
      return value !== undefined && value !== null && value !== '';
    });

    return matchedKey ? source[matchedKey] : undefined;
  }, undefined);
};

const matchesDobField = buildFieldMatcher(DOB_FIELD_TOKENS);

const extractPatientDob = (patient) => (
  coalescePatientValue(patient, [
    'date_of_birth',
    'dateOfBirth',
    'dob',
    'birth_date',
    'birthDate',
    'patientDateOfBirth',
  ])
);

const extractPatientMrn = (patient) => (
  coalescePatientValue(patient, [
    'mrn',
    'MRN',
    'medical_record_number',
    'medicalRecordNumber',
  ])
);

const derivePatientIdentifierDisplay = (patientId, mrnValue) => {
  const normalizedMrn = typeof mrnValue === 'string' ? mrnValue.trim() : mrnValue;
  if (normalizedMrn !== undefined && normalizedMrn !== null && normalizedMrn !== '') {
    return String(normalizedMrn);
  }

  if (patientId === undefined || patientId === null) {
    return patientId;
  }

  const normalizedId = typeof patientId === 'string' ? patientId.trim() : patientId;
  if (normalizedId === '') {
    return normalizedId;
  }

  return String(normalizedId);
};

const getSectionIconType = (sectionTitle) => {
  const title = (sectionTitle || '').toLowerCase();
  if (title.includes('patient')) {
    return 'patient';
  }
  if (title.includes('document') || title.includes('file')) {
    return 'document';
  }
  if (title.includes('folder') || title.includes('category')) {
    return 'folder';
  }
  return 'list';
};

const getWorkflowIconType = (workflow, title) => {
  const workflowLower = (workflow || '').toLowerCase();
  const workflowIconType = WORKFLOW_ICON_TYPES[workflowLower];
  if (workflowIconType) {
    return workflowIconType;
  }

  const titleLower = (title || '').toLowerCase();

  if (titleLower.includes('task')) {
    return 'task';
  }
  if (titleLower.includes('create')) {
    return 'create';
  }
  return 'send';
};

// A field may declare that it adopts a sibling in its own row:
// `adopts_field: <internal_name>` in the schema. The owner then renders that
// field's control inside its own layout, and the drawer stops laying the field
// out itself.
//
// The drawer resolves the pairing and nothing more — it never learns what an
// owner does with what it adopts, so any component able to accept a slot can use
// this without the drawer growing a branch for it.
//
// Returns the adopted field per owner (keyed by the owner's internal_name) plus
// the set of adopted names, so a row renderer can tell the two roles apart in a
// single pass.
const resolveFieldSlots = (fieldsInRow = []) => {
  const fields = (fieldsInRow || []).filter(Boolean);
  const adoptedByOwner = {};
  const adoptedNames = new Set();

  fields.forEach((owner) => {
    const adoptedName = owner.adopts_field;
    if (!adoptedName) return;

    const adopted = fields.find((field) => field.internal_name === adoptedName);
    // A declaration that does not resolve inside this row leaves both fields laid
    // out normally. The schema is stale or the row was overridden, and
    // suppressing either one would hide a control with nothing to replace it.
    if (!adopted || adopted === owner) return;

    adoptedByOwner[owner.internal_name] = adopted;
    adoptedNames.add(adopted.internal_name);
  });

  return { adoptedByOwner, adoptedNames };
};

// ENG-3934: the values a held document's saved Send to Chart form pre-fills
// directly, by field name. `omit` names the fields restored another way (the
// NextGen document, the task template) or picked again (a patient that must
// come from search results).
const savedFormPrefillValues = (savedForm, { omit = [] } = {}) => {
  const values = savedForm?.values;
  if (!values || typeof values !== 'object') return {};

  const omitted = new Set(['nextgen_task_template', ...omit]);
  return Object.fromEntries(Object.entries(values).filter(([name]) => !omitted.has(name)));
};

export {
  buildFieldMatcher,
  coalescePatientValue,
  derivePatientIdentifierDisplay,
  extractPatientDob,
  extractPatientMrn,
  getSectionIconType,
  getWorkflowIconType,
  matchesDobField,
  parseDateFieldValue,
  resolveFieldSlots,
  savedFormPrefillValues,
};
